import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import {
  login,
  register,
  UserSession
} from '../utils'
import { appUrl } from "../appwrite/methods"
import logo from "../assets/tom-logo-icon-black.jpg"
import { ID, Client, Account, Databases, Functions } from "appwrite";
import { AppProvider } from '@toolpad/core/AppProvider';
import { SignInPage } from '@toolpad/core/SignInPage';
import { useTheme, createTheme } from '@mui/material/styles';
import {
  FormControl,
  TextField,
  Link,
  Button,
  Stack,
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';


const client = new Client()
  .setEndpoint(process.env.REACT_APP_ENDPOINT)
  .setProject(process.env.REACT_APP_PROJECT_ID);

const users = new Account(client);
const databases = new Databases(client);
const functions = new Functions(client);


function LogoButton({ redirect }) {
  const navigate = useNavigate();

  return (
    <Button
      variant="text"
      onClick={() => navigate(redirect || '/login')}
      sx={{
        position: 'relative',
        display: 'flex',
        mt: '-5%',
        mb: '5%',
        height: '2.5em',
        width: 'fit-content',
        alignItems: 'center'
      }}
    >
      <ArrowBackIosNewOutlinedIcon sx={{ mr: '0.5em', fontSize: '1em' }} />
      Back&nbsp;
    </Button>
  );
}

// const providers = [{ id: 'nodemailer', name: 'Email' }];
const providers = [{ id: 'credentials', name: 'Email and Password' }];

const BRANDING = {
  logo: (<>
    <LogoButton />
    <Stack direction="row" justifyContent="center" alignItems="center" gap={1.5}>
      <img
        src={logo}
        alt="Takeoff Monkey Widgets Logo"
        style={{ height: 48 }}
      />
    </Stack>
  </>),
  title: 'Takeoff Monkey Widgets',
};


export default function ResetPassword() {
  const navigate = useNavigate()
  const [session, setSession] = useAtom(UserSession)

  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("info");
  const [creating, setCreating] = useState(false);

  const email = useRef();
  const password = useRef();
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const toggleShowPassword1 = () => setShowPassword1((show) => !show);
  const handleMouseDownPassword1 = (event) => event.preventDefault();
  const toggleShowPassword2 = () => setShowPassword2((show) => !show);
  const handleMouseDownPassword2 = (event) => event.preventDefault();

  const urlParams = new URLSearchParams(window.location.search);
  const userID = urlParams.get('userId');
  const secret = urlParams.get('secret');


  if (session) {
    navigate('/');
  }
  
  const handleReset = async (formData, callbackUrl) => {
    try {
      setAlertType("info");
      setCreating(true);

      // Changing password
      if (userID && secret) {
        if (email.current?.value !== password.current?.value) {
          setAlertType("warning");
          setOpen("Your new password does not match.");
          setCreating(false);
          return;
        }

        setOpen("Resetting password...");
        await functions.createExecution(
          "user-reset-password",
          JSON.stringify({
            'userId': userID,
            'secret': secret,
            'password': password.current?.value
          }),
          false,
          '/',
          'GET'
        ).then(() => {
          setCreating(false);
          navigate('/login');
        })
      
      // Sending reset email
      } else {
        if (!email.current?.value) {
          setAlertType("warning");
          setOpen("Please enter your account email.");
          setCreating(false);
          return;
        }

        setOpen("Sending password reset email...");
        await functions.createExecution(
          "reset-password-email",
          JSON.stringify({
            "email": email.current?.value,
            "url": `${appUrl}/reset-password`,
          }),
          false,
          '/',
          'GET'
        ).then(() => {
          setOpen("Reset email sent! Please check your inbox.");
          setCreating(false);
        })
      }
    } catch (error) {
      console.error(error);
      setAlertType("error");
      setOpen("Error sending reset email: " + error.message);
      setCreating(false);
    }
  }


  return (<>
    <AppProvider branding={BRANDING} >
      <Stack direction="row" sx={{ maxWidth: '500px', mx: 'auto' }}>
        <SignInPage
          signIn={handleReset}
          providers={providers}
          slotProps={{
            emailField: {
              sx: { '& .MuiInputBase-root': { height: '2.5em', overflow: 'hidden' } }
            },
          }}
          slots={{
            title: () => <Typography variant="h4" sx={{ mt: 1, mb: 1, textAlign: 'center' }}>Reset password</Typography>,
            subtitle: () => <Typography variant="body1" sx={{ mb: 1.5, color: 'text.secondary', textAlign: 'center' }}>
              {userID && secret ? "Enter a new account password" : "Enter your account email"}
            </Typography>,
            emailField: () => userID && secret ? <FormControl
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '2.5em',
                  fontSize: '1em',
                  overflow: 'hidden'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1em',
                  top: '-6px',
                },
                '& .MuiInputLabel-shrink': {
                  top: 0,
                },
              }}
            >
              <InputLabel htmlFor="outlined-adornment-password">New password</InputLabel>
              <OutlinedInput
                id="outlined-adornment-password"
                inputRef={email}
                defaultValue={email.current?.value}
                type={showPassword1 ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword1 ? 'hide password' : 'show password'}
                      onClick={toggleShowPassword1}
                      onMouseDown={handleMouseDownPassword1}
                      edge="end"
                      size="small"
                      sx={{ mr: '7px' }}
                    >
                      {showPassword1 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                size="small"
              />
            </FormControl> : <TextField
              autoComplete
              inputRef={email}
              defaultValue={email.current?.value}
              fullWidth
              label="Email"
              variant="outlined"
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { height: '2.5em', overflow: 'hidden' } }}
            />,
            passwordField: () => userID && secret ? <FormControl
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '2.5em',
                  fontSize: '1em',
                  overflow: 'hidden'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1em',
                  top: '-6px',
                },
                '& .MuiInputLabel-shrink': {
                  top: 0,
                },
              }}
            >
              <InputLabel htmlFor="outlined-adornment-password">Repeat password</InputLabel>
              <OutlinedInput
                id="outlined-adornment-password"
                inputRef={password}
                defaultValue={password.current?.value}
                type={showPassword2 ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword2 ? 'hide password' : 'show password'}
                      onClick={toggleShowPassword2}
                      onMouseDown={handleMouseDownPassword2}
                      edge="end"
                      size="small"
                      sx={{ mr: '7px' }}
                    >
                      {showPassword2 ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                size="small"
              />
            </FormControl> : null,
            rememberMe: () => null,
            submitButton: () => <Button variant="contained" fullWidth size="large" sx={{ mt: 2 }} onClick={() => handleReset()}>
              {creating ? <CircularProgress size={25} color='inherit' /> : userID && secret ? "Reset password" : "Send reset email"}
            </Button>
          }}
        />
      </Stack>
    </AppProvider>

    <Snackbar
      open={Boolean(open)}
      onClose={() => setOpen(false)}
      autoHideDuration={10000}
      key="snackbar"
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity={alertType}>
        {/* <AlertTitle>Error</AlertTitle> */}
        {open}
      </Alert>
    </Snackbar>
  </>);
}